import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Search from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'next-i18next';
import { useFilters } from '../../Provider';

interface CategoryItem {
  id: number;
  name: string;
}

const LotCategoriesFilter: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const [selectedItems, setSelectedItems] = useState<CategoryItem[]>([]);

  const theme = useTheme();
  const { t } = useTranslation('tenders');
  const {
    selectedFilters,
    setField,
    categories,
    isLoadingCategories,
    debouncedSearchCategory,
  } = useFilters();

  const current = selectedFilters.category;

  useEffect(() => {
    if (categories?.length) {
      const toAdd = (categories as CategoryItem[])
        .filter((c) => current?.includes(Number(c.id)))
        .filter((c) => !selectedItems.some((item) => item.id === c.id))
        .map((c) => ({ id: c.id, name: c.name }));

      if (toAdd.length > 0) {
        setSelectedItems((prev) => [...prev, ...toAdd]);
      }
    }
  }, [categories, current, selectedItems]);

  const handleAdd = (c: CategoryItem) => {
    if (!current?.includes(c.id)) {
      setField('category', [...(current ?? []), c.id]);
      setSelectedItems((prev) => [...prev, c]);
    }
  };

  const handleRemove = (id: number) => {
    setField(
      'category',
      current?.filter((cId) => cId !== id),
    );
    setSelectedItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInputValue(v);
    debouncedSearchCategory(v);
    setOpen(true);
  };

  return (
    <Stack
      direction={{ lg: 'row', md: 'column', xs: 'column' }}
      mt={3.75}
      gap={{ xs: 1.25, lg: 2.5 }}
      alignItems="baseline"
    >
      <Box width="100%" maxWidth={120}>
        <Typography fontWeight={700}>{t('tenders.modal.category')}</Typography>
      </Box>

      {isLoadingCategories ? (
        <CircularProgress />
      ) : (
        <Stack direction="column" width="100%" gap={2.5}>
          <Box width="100%">
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  value={inputValue}
                  onClick={() => setOpen(true)}
                  onChange={handleInputChange}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                {open && (
                  <Box
                    sx={{
                      overflow: 'auto',
                      backgroundColor: 'white',
                      zIndex: 999,
                      maxHeight: '200px',
                      position: 'absolute',
                      width: '100%',
                      borderRadius: '4px',
                      boxShadow: '0px 2px 5px -3px rgba(0,0,0,0.7)',
                    }}
                  >
                    <List disablePadding>
                      {categories?.length === 0 ? (
                        <ListItem>
                          <ListItemText disableTypography>
                            {t('common:common.notFound')}
                          </ListItemText>
                        </ListItem>
                      ) : (
                        categories
                          ?.filter((c) => !current?.includes(Number(c.id)))
                          .map((c) => (
                            <ListItemButton
                              key={c.id}
                              onClick={() =>
                                handleAdd({
                                  id: Number(c.id),
                                  name: `${c.name}`,
                                })
                              }
                            >
                              <ListItemText disableTypography>
                                {c.name}
                              </ListItemText>
                            </ListItemButton>
                          ))
                      )}
                    </List>
                  </Box>
                )}
              </Box>
            </ClickAwayListener>

            <Stack direction="row" flexWrap="wrap" gap={1.25} mt={2.5}>
              {current?.map((catId) => {
                const name =
                  categories?.find((c) => c.id === catId)?.name ||
                  selectedItems.find((c) => c.id === catId)?.name ||
                  '';
                return (
                  <Box
                    key={catId}
                    sx={{
                      maxHeight: { lg: '70px', xs: '60px', md: '60px' },
                      height: '100%',
                      border: `2px solid ${theme.palette.primary.light}`,
                      padding: '4px 8px',
                      borderRadius: '4px',
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={3.75}
                    >
                      <Typography variant="body1">
                        {name.length > 30 ? `${name.slice(0, 30)}...` : name}
                      </Typography>
                      <IconButton
                        sx={{ padding: 0 }}
                        onClick={() => handleRemove(catId)}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

export default LotCategoriesFilter;
